import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import {
  ConfirmAllocationPaymentRequest,
  ConfirmDepositPaymentRequest,
  ConfirmTransferPaymentRequest,
  GetAllocationPaymentTargetAccountsRequest,
  GetDepositPaymentTargetAccountsRequest,
  GetTransferPaymentTargetAccountsRequest,
  PaymentResource,
  SubmitAllocationPaymentRequest,
  SubmitDepositPaymentRequest,
  SubmitTransferPaymentRequest,
} from '../../resources/payment.resource';
import {
  Currency,
  GetAllocationPaymentDetailsResponse,
  GetDepositPaymentDetailsResponse,
  GetTransferPaymentDetailsResponse,
} from '../../types';
import { BaseCurrency } from '../../utils';
import { GetResourceHeaderWithToken } from '../useResource';

export type UsePaymentCommonProps = {
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  paymentResource: PaymentResource;
};

export const depositInternalCurrencyListDefault = [BaseCurrency, 'EUR', 'USD', 'GBP', 'CHF'] as Array<Currency>;

export const usePaymentCommon = ({ getResourceHeaderWithToken, paymentResource }: UsePaymentCommonProps) => {
  const getDepositPaymentDetails = useCallback(
    (clientId: string): Promise<AxiosResponse<GetDepositPaymentDetailsResponse, any>> => {
      return paymentResource.getDepositPaymentDetails({ clientId }, getResourceHeaderWithToken());
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  // Deposit
  const getDepositPaymentTargetAccounts = useCallback(
    (
      { clientId, currencyCode, accountNumber }: GetDepositPaymentTargetAccountsRequest,
      controller: AbortController,
    ) => {
      return paymentResource.getDepositPaymentTargetAccounts(
        {
          clientId,
          currencyCode,
          accountNumber,
        },
        getResourceHeaderWithToken(),
        controller,
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  const confirmDepositPayment = useCallback(
    ({ tempId, confirmDepositPaymentRequest }: ConfirmDepositPaymentRequest) => {
      return paymentResource.confirmDepositPayment(
        {
          confirmDepositPaymentRequest,
          tempId,
        },
        getResourceHeaderWithToken(),
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  const submitDepositPayment = useCallback(
    ({ submitDepositPaymentRequest }: SubmitDepositPaymentRequest) => {
      return paymentResource.submitDepositPayment(
        {
          submitDepositPaymentRequest,
        },
        getResourceHeaderWithToken(),
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  // Transfer
  const getTransferPaymentDetails = useCallback(
    (clientId: string): Promise<AxiosResponse<GetTransferPaymentDetailsResponse, any>> => {
      return paymentResource.getTransferPaymentDetails({ clientId }, getResourceHeaderWithToken());
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  const getTransferPaymentTargetAccounts = useCallback(
    (
      { clientId, currencyCode, accountCategory }: GetTransferPaymentTargetAccountsRequest,
      controller: AbortController,
    ) => {
      return paymentResource.getTransferPaymentTargetAccounts(
        {
          clientId,
          currencyCode,
          accountCategory,
        },
        getResourceHeaderWithToken(),
        controller,
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  const confirmTransferPayment = useCallback(
    ({ tempId, confirmTransferPaymentRequest }: ConfirmTransferPaymentRequest) => {
      return paymentResource.confirmTransferPayment(
        {
          tempId,
          confirmTransferPaymentRequest,
        },
        getResourceHeaderWithToken(),
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  const submitTransferPayment = useCallback(
    ({ submitTransferPaymentRequest }: SubmitTransferPaymentRequest) => {
      return paymentResource.submitTransferPayment(
        {
          submitTransferPaymentRequest,
        },
        getResourceHeaderWithToken(),
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  // Allocation
  const getAllocationPaymentDetails = useCallback(
    (clientId: string): Promise<AxiosResponse<GetAllocationPaymentDetailsResponse, any>> => {
      return paymentResource.getAllocationPaymentDetails({ clientId }, getResourceHeaderWithToken());
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  const getAllocationPaymentTargetAccounts = useCallback(
    (
      { clientId, currencyCode, accountCategory }: GetAllocationPaymentTargetAccountsRequest,
      controller: AbortController,
    ) => {
      return paymentResource.getAllocationPaymentTargetAccounts(
        {
          clientId,
          currencyCode,
          accountCategory,
        },
        getResourceHeaderWithToken(),
        controller,
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  const confirmAllocationPayment = useCallback(
    ({ tempId, confirmAllocationPaymentRequest }: ConfirmAllocationPaymentRequest) => {
      return paymentResource.confirmAllocationPayment(
        { tempId, confirmAllocationPaymentRequest },
        getResourceHeaderWithToken(),
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  const submitAllocationPayment = useCallback(
    ({ submitAllocationPaymentRequest }: SubmitAllocationPaymentRequest) => {
      return paymentResource.submitAllocationPayment(
        {
          submitAllocationPaymentRequest,
        },
        getResourceHeaderWithToken(),
      );
    },
    [getResourceHeaderWithToken, paymentResource],
  );

  return {
    getDepositPaymentDetails,
    getDepositPaymentTargetAccounts,
    confirmDepositPayment,
    submitDepositPayment,
    getTransferPaymentDetails,
    getTransferPaymentTargetAccounts,
    confirmTransferPayment,
    submitTransferPayment,
    getAllocationPaymentDetails,
    getAllocationPaymentTargetAccounts,
    confirmAllocationPayment,
    submitAllocationPayment,
  };
};
